<template>
  <div v-if="mostrarModalMotivosCancelacion">
    <p class="p text-primary mb-3">
      {{ $t('reserva.msjSolucionReserva') }}
    </p>

    <div class="d-flex flex-column gap-20">
      <div v-for="motivo in motivos" :key="motivo.id" class="d-flex flex-column gap-10">
        <input
          :id="'radio' + motivo.id"
          v-model="motivoSeleccionado"
          :value="motivo"
          class="d-none"
          type="radio"
          name="flexRadioDefault" />
        <label class="w-100 d-flex align-items-center pointer gap-10 mb-0" :for="'radio' + motivo.id">
          <RadioButtonIcon :is-selected="motivoSeleccionado?.id === motivo.id" />
          <span class="p text-primary">{{ motivo.motivo }}</span>
        </label>
        <div
          v-if="motivoSeleccionado?.id === motivo.id"
          class="solucion-box ms-4 p-10 rounded-12 d-flex flex-column gap-10">
          <p class="small semibold text-primary mb-0">
            {{ $t('general.solucion') }}
          </p>
          <p class="small text-primary mb-0">
            {{ motivoSeleccionado.solucion }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="mostrarModalAvisoCancelarAllReservas">
    <p class="p text-primary pb-3">
      {{ $t('reserva.confirmarCancelacionCupoIntegrado') }}
    </p>
    <p class="p text-primary">
      {{ $t('reserva.msjCancelacionCupoIntegrado', { nombresReservasHabitacion: getAllNamesReservasHabitacion() }) }}
    </p>
  </div>

  <div v-else class="d-flex flex-column gap-20">
    <div class="d-flex flex-column gap-20">
      <TarjetaProximaReservaAcciones
        v-for="reserva in reservasActivas"
        :key="reserva.id"
        :reserva="reserva"
        :grupo="grupo"
        :selectable="reservasActivas.length > 1"
        :selected="reservasSeleccionadas.includes(reserva.id)"
        :action="ACCIONES_TARJETA_RESERVA.CANCEL"
        @toggle="toggleSeleccionReserva(reserva.id)" />
    </div>

    <div class="condiciones-box p-20 rounded-12 d-flex flex-column gap-20">
      <div class="d-flex justify-content-between align-items-center gap-20 pointer" @click="toggleCollapse()">
        <div class="h6 semibold text-primary mb-0">{{ $t('reserva.condicionesGastosCancelacion') }}</div>
        <button type="button" class="btn bg-transparent border-0 p-0">
          <i class="fa-solid fa-chevron-up condiciones-chevron" :class="{ 'condiciones-chevron-collapsed': isCollapsed }" />
        </button>
      </div>

      <div v-show="!isCollapsed">
        <template v-if="!grupo.viaje.buscador">
          <PoliticasCancelacion
            :politica-cancelacion="grupo.politicaCancelacion"
            :fecha-limite-pago="getFechaLimitePago()"
            :precio-total="grupo.reservas[0].precioTotal" />
        </template>
        <template v-else>
          <h5 class="h6 text-secondary bold">
            {{ $t('home.form.policy.cancelReserva') }}
          </h5>
          <span v-sanitize-html="politicasCancelacion" class="p text-primary" />
          <p class="p text-primary">{{ $t('home.form.policy.detail4Condiciones') }}</p>
          <p class="p text-primary">* {{ $t('home.form.policy.detail5Condiciones') }}</p>
        </template>
      </div>
    </div>
  </div>

  <div class="d-flex flex-wrap justify-content-end gap-20 mt-4">
    <template v-if="mostrarModalMotivosCancelacion">
      <MyGocuButton secondary :label="$t('botones.cancelarReserva')" @click="cancelarReservaConfirmacion()" />
      <MyGocuButton primary :label="$t('reserva.mantenerReserva')" @click="closeModal()" />
    </template>
    <template v-else-if="mostrarModalAvisoCancelarAllReservas">
      <MyGocuButton secondary :label="$t('botones.descartar')" @click="closeModal()" />
      <MyGocuButton primary :label="$t('botones.cancelarReserva')" @click="cancelarReservaConfirmacion()" />
    </template>
    <template v-else>
      <MyGocuButton secondary :label="$t('botones.cancelarReserva')" @click="realizarCancelaciones()" />
      <MyGocuButton primary :label="$t('botones.descartar')" @click="closeModal()" />
    </template>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import TarjetaProximaReservaAcciones from '../tarjetas/TarjetaProximaReservaAcciones.vue';
  import PoliticasCancelacion from '@/components/misc/PoliticasCancelacion.vue';
  import { useI18n } from 'vue-i18n';
  import { obtenerPoliticasCancelacion } from '@/helpers/htmlMakers';
  import { alertAutoCloseSuccessWithoutCallback } from '@/services/utils/AlertUtils';
  import { createToaster } from '@meforma/vue-toaster';
  import ReservasApiService from '@/services/api/ReservasApiService';
  import { HITOS_PAGO_TIPOCANTIDAD } from '@/constants/viajeConstants';
  import { ESTADOS_RESERVA, ACCIONES_TARJETA_RESERVA } from '@/constants/reservaConstants';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import RadioButtonIcon from '@/components/Icons/RadioButtonIcon.vue';

  const props = defineProps({
    grupo: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(['motivosCancelacion', 'close']);

  const { locale, t } = useI18n();
  const politicasCancelacion = ref('');
  const listaReservasCancelacion = ref([]);
  const motivoSeleccionado = ref(null);
  const mostrarModalMotivosCancelacion = ref(false);
  const mostrarModalAvisoCancelarAllReservas = ref(false);
  const isCollapsed = ref(true);
  const reservasCancelar = ref(props.grupo.reservas);
  const reservasSeleccionadas = ref([]);
  const motivos = ref([]);
  const toaster = createToaster({ position: 'top' });

  const reservasActivas = computed(() =>
    reservasCancelar.value.filter(reserva => reserva.estado !== ESTADOS_RESERVA.CANCELADA)
  );

  function toggleSeleccionReserva(reservaId) {
    const index = reservasSeleccionadas.value.indexOf(reservaId);
    if (index === -1) {
      reservasSeleccionadas.value.push(reservaId);
    } else {
      reservasSeleccionadas.value.splice(index, 1);
    }
  }

  function getFechaLimitePago() {
    return props.grupo.viaje.hitosPago.find(hito => hito.tipoCantidad === HITOS_PAGO_TIPOCANTIDAD.RESTANTE)?.fecha;
  }

  function actualizarPoliticasCancelacion() {
    politicasCancelacion.value = obtenerPoliticasCancelacion(props.grupo, false);
  }

  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value;
  }

  function getAllNamesReservasHabitacion() {
    const reservasConCupoIntegrado = props.grupo.reservas.filter(reserva => reserva.integrado);
    return reservasConCupoIntegrado.map(reserva => `${reserva.nombre} ${reserva.apellidos}`).join('<br/>');
  }

  function realizarCancelaciones() {
    const reservasCuposIntegrados = props.grupo.reservas.filter(reserva => reserva.integrado);
    if (reservasCuposIntegrados.length > 1) {
      mostrarModalAvisoCancelarAllReservas.value = true;
    } else {
      listaReservasCancelacion.value =
        reservasActivas.value.length > 1
          ? reservasSeleccionadas.value
          : reservasActivas.value.map(reserva => reserva.id);
      if (listaReservasCancelacion.value.length === 0) {
        toaster.error(t('errors.seleccionaReservaContinuar'));
      } else {
        mostrarModalMotivosCancelacion.value = true;
      }
    }
  }

  function closeModal() {
    listaReservasCancelacion.value = [];
    reservasSeleccionadas.value = [];
    motivoSeleccionado.value = null;
    mostrarModalMotivosCancelacion.value = false;
    mostrarModalAvisoCancelarAllReservas.value = false;
    emit('close');
  }

  function cancelarReservaConfirmacion() {
    if (mostrarModalAvisoCancelarAllReservas.value) {
      mostrarModalAvisoCancelarAllReservas.value = false;
      listaReservasCancelacion.value = props.grupo.reservas.map(reserva => reserva.id);
      mostrarModalMotivosCancelacion.value = true;
      return;
    }
    if (!motivoSeleccionado.value) {
      toaster.error('Selecciona algun motivo para continuar');
      return;
    }

    ReservasApiService.doCancelarReservasGrupo(
      props.grupo.id,
      listaReservasCancelacion.value,
      motivoSeleccionado.value.id.toString(10),
      '123123'
    ).then(() => {
      listaReservasCancelacion.value.forEach(reserva => {
        const indexReserva = reservasCancelar.value.findIndex(val => val.id === reserva);
        if (indexReserva !== -1) {
          reservasCancelar.value[indexReserva].estado = ESTADOS_RESERVA.CANCELADA;
        }
      });
      alertAutoCloseSuccessWithoutCallback();
      emit('close');
      window.location.reload();
    });
  }

  watch(locale, () => {
    actualizarPoliticasCancelacion();
  });

  watch(mostrarModalMotivosCancelacion, value => {
    emit('motivosCancelacion', value);
  });

  onBeforeMount(() => {
    actualizarPoliticasCancelacion();
    ReservasApiService.getMotivosCancelacion().then(res => (motivos.value = res));
  });
</script>

<style lang="css" scoped>
  .condiciones-box {
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .solucion-box {
    background: rgba(var(--bs-primary-rgb), 0.1);
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .condiciones-chevron {
    transition: transform 0.3s;
  }

  .condiciones-chevron-collapsed {
    transform: rotate(180deg);
  }
</style>
